// WorkspaceGuide — Permanent learning guide as workspace page 1
//
// Explains the three teaching forms, how the concept map works as
// central artifact, voice availability, and connection to preparation.
// Contains two dynamic elements: Bloom progress bar and voice status.

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Node } from '@xyflow/react';
import type { TeachingForm, BloomLevel } from '../../types';
import Button from '../ui/Button';

// Duplicated from WorkspaceSidebar — same visual representation
const BLOOM_ORDER: BloomLevel[] = ['recognize', 'understand', 'apply', 'analyze', 'evaluate', 'create'];
const BLOOM_COLORS: Record<BloomLevel, string> = {
  recognize: 'bg-gray-500',
  understand: 'bg-blue-500',
  apply: 'bg-green-500',
  analyze: 'bg-yellow-500',
  evaluate: 'bg-orange-500',
  create: 'bg-red-500',
};

function getBloomDistribution(nodes: Node[]): Record<BloomLevel, number> {
  const dist: Record<BloomLevel, number> = {
    recognize: 0, understand: 0, apply: 0, analyze: 0, evaluate: 0, create: 0,
  };
  for (const node of nodes) {
    const level = (node.data as { bloomLevel?: string })?.bloomLevel as BloomLevel | undefined;
    if (level && level in dist) {
      dist[level]++;
    }
  }
  return dist;
}

interface TeachingFormCard {
  form: TeachingForm;
  colorBg: string;
  colorBorder: string;
  colorText: string;
}

const FORM_CARDS: TeachingFormCard[] = [
  { form: 'introduction', colorBg: 'bg-amber-950/30', colorBorder: 'border-amber-500/50', colorText: 'text-amber-400' },
  { form: 'dialog', colorBg: 'bg-blue-950/30', colorBorder: 'border-blue-500/50', colorText: 'text-blue-400' },
  { form: 'exam', colorBg: 'bg-indigo-950/30', colorBorder: 'border-indigo-500/50', colorText: 'text-indigo-400' },
];

interface WorkspaceGuideProps {
  topicTitle: string;
  topicDescription?: string;
  conceptMapNodes: Node[];
  isCreating: boolean;
  onSelectMode: (form: TeachingForm) => void;
  onGoToMap: () => void;
}

export default function WorkspaceGuide({
  topicTitle,
  topicDescription,
  conceptMapNodes,
  isCreating,
  onSelectMode,
  onGoToMap,
}: WorkspaceGuideProps) {
  const { t } = useTranslation('session');
  const navigate = useNavigate();
  const bloomDist = getBloomDistribution(conceptMapNodes);
  const totalNodes = conceptMapNodes.length;

  // Voice status — check browser mediaDevices support
  const [micAvailable, setMicAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicAvailable(false);
      return;
    }
    // Check permission status without triggering a prompt
    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: 'microphone' as PermissionName })
        .then((status) => setMicAvailable(status.state === 'granted'))
        .catch(() => {
          // permissions.query not supported for microphone — assume available if API exists
          setMicAvailable(true);
        });
    } else {
      // Fallback: mediaDevices exists so mic is likely available
      setMicAvailable(true);
    }
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-gray-950 p-8 md:p-12">
      <div className="max-w-3xl mx-auto space-y-10">

        {/* 1. Topic header with dynamic status */}
        <header>
          <h1 className="text-2xl font-display font-semibold text-white mb-2">
            {topicTitle}
          </h1>
          {topicDescription && (
            <p className="text-sm text-gray-400 leading-relaxed mb-4">
              {topicDescription}
            </p>
          )}

          <div className="flex items-center gap-6">
            {/* Bloom progress bar */}
            {totalNodes > 0 && (
              <div className="flex-1">
                <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-gray-800">
                  {BLOOM_ORDER.map(level => {
                    const count = bloomDist[level];
                    if (count === 0) return null;
                    const pct = (count / totalNodes) * 100;
                    return (
                      <div
                        key={level}
                        className={`${BLOOM_COLORS[level]} transition-all duration-300`}
                        style={{ width: `${pct}%` }}
                        title={`${level}: ${count}`}
                      />
                    );
                  })}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {t('workspace.conceptCount', { count: totalNodes })}
                </div>
              </div>
            )}

            {/* Voice status indicator */}
            {micAvailable !== null && (
              <div className="flex items-center gap-4 shrink-0">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${micAvailable ? 'bg-green-500' : 'bg-gray-600'}`} />
                  <span className="text-xs text-gray-400">
                    {t(micAvailable ? 'workspace.guide.voiceStatus.ready' : 'workspace.guide.voiceStatus.notConfigured')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500" />
                  <span className="text-xs text-gray-400">
                    {t('workspace.guide.voiceStatus.ttsInfo')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* 2. Learn with Anna */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-2">
            {t('workspace.guide.learnWithAnna.title')}
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed">
            {t('workspace.guide.learnWithAnna.description')}
          </p>
        </section>

        {/* 3. Three teaching forms */}
        <section>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {FORM_CARDS.map(({ form, colorBg, colorBorder, colorText }) => (
              <div
                key={form}
                className={`rounded-xl border ${colorBorder} ${colorBg} p-5 flex flex-col`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">
                    {t(`workspace.guide.teachingForms.${form}.emoji`)}
                  </span>
                  <h3 className={`font-semibold ${colorText}`}>
                    {t(`workspace.guide.teachingForms.${form}.title`)}
                  </h3>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed mb-3 flex-1">
                  {t(`workspace.guide.teachingForms.${form}.description`)}
                </p>
                <div className="text-xs text-gray-500 mb-4">
                  {t(`workspace.guide.teachingForms.${form}.interaction`)}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSelectMode(form)}
                  disabled={isCreating}
                  className="w-full"
                >
                  {isCreating ? t('workspace.creatingSession') : t('workspace.guide.startSession')}
                </Button>
              </div>
            ))}
          </div>
        </section>

        {/* 4. Canvas — your concept map */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-2">
            {t('workspace.guide.canvas.title')}
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed mb-3">
            {t('workspace.guide.canvas.description')}
          </p>
          <ul className="text-sm text-gray-400 space-y-1 mb-4">
            <li className="flex items-center gap-2">
              <span className="text-amber-400">🌱</span>
              {t('workspace.guide.canvas.editIntro')}
            </li>
            <li className="flex items-center gap-2">
              <span className="text-blue-400">💬</span>
              {t('workspace.guide.canvas.developDialog')}
            </li>
            <li className="flex items-center gap-2">
              <span className="text-indigo-400">🎯</span>
              {t('workspace.guide.canvas.readonlyExam')}
            </li>
          </ul>
          <Button
            variant="outline"
            size="sm"
            onClick={onGoToMap}
          >
            {t('workspace.guide.canvas.goToMap')}
          </Button>
        </section>

        {/* 5. From session to preparation */}
        <section>
          <h2 className="text-lg font-semibold text-white mb-2">
            {t('workspace.guide.preparation.title')}
          </h2>
          <p className="text-sm text-gray-400 leading-relaxed mb-4">
            {t('workspace.guide.preparation.description')}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/preparation')}
          >
            {t('workspace.guide.preparation.goToPreparation')}
          </Button>
        </section>

      </div>
    </div>
  );
}
